import apiClient from './client';
import type { Cliente, ClienteList } from '../types/cliente';

export interface ClienteCreatePayload {
  ragione_sociale: string;
  piva?: string;
  codice_fiscale?: string;
  indirizzo?: string;
  cap?: string;
  citta?: string;
  provincia?: string;
  nazione?: string;
  codice_destinatario?: string;
  referente?: string;
  telefono?: string;
  email: string;
  pec?: string;
}

/**
 * PATCH parziale. Il backend usa `exclude_unset`: chiave ASSENTE = campo
 * invariato, chiave a `null` = cancellazione esplicita. Vanno quindi inviate
 * esplicitamente tutte le chiavi annullabili, anche quelle non modificate.
 *
 * `ragione_sociale`, `email`, `nazione` e `codice_destinatario` sono NOT NULL
 * a schema: si inviano solo se valorizzate, mai a `null` (il backend
 * risponderebbe 422).
 */
export interface ClienteUpdatePayload {
  ragione_sociale?: string;
  piva?: string | null;
  codice_fiscale?: string | null;
  indirizzo?: string | null;
  cap?: string | null;
  citta?: string | null;
  provincia?: string | null;
  nazione?: string;
  codice_destinatario?: string;
  referente?: string | null;
  telefono?: string | null;
  email?: string;
  pec?: string | null;
  /** Conferma della modifica dei dati fiscali su cliente già fatturato (409). */
  conferma_modifica_fiscale?: boolean;
}

export interface ClienteFilters {
  page?: number;
  limit?: number;
  search?: string;
  include_deleted?: boolean;
}

export async function listClienti(filters?: ClienteFilters): Promise<ClienteList> {
  const params: Record<string, string | number | boolean> = {};
  if (filters?.page) params['page'] = filters.page;
  if (filters?.limit) params['limit'] = filters.limit;
  if (filters?.search) params['search'] = filters.search;
  if (filters?.include_deleted) params['include_deleted'] = filters.include_deleted;
  const { data } = await apiClient.get<ClienteList>('/api/customers', { params });
  return data;
}

export async function getCliente(id: string): Promise<Cliente> {
  const { data } = await apiClient.get<Cliente>(`/api/customers/${id}`);
  return data;
}

export async function createCliente(payload: ClienteCreatePayload): Promise<Cliente> {
  const { data } = await apiClient.post<Cliente>('/api/customers', payload);
  return data;
}

export async function updateCliente(id: string, payload: ClienteUpdatePayload): Promise<Cliente> {
  const { data } = await apiClient.patch<Cliente>(`/api/customers/${id}`, payload);
  return data;
}

export async function deleteCliente(id: string): Promise<{ deleted: boolean; id: string }> {
  const { data } = await apiClient.delete<{ deleted: boolean; id: string }>(`/api/customers/${id}`);
  return data;
}

export async function searchClienti(search: string, limit = 10): Promise<Cliente[]> {
  const { data } = await apiClient.get<ClienteList>('/api/customers', {
    params: { search, limit },
  });
  return data.items;
}
